import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, Float, OrbitControls, Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

// Floating particles around the sun
function SunParticles() {
  const count = 50;
  const mesh = useRef();
  
  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 1.5 + Math.random() * 3;
      temp.push({
        position: [
          Math.cos(angle) * radius,
          (Math.random() - 0.5) * 2,
          Math.sin(angle) * radius
        ],
        scale: 0.02 + Math.random() * 0.03,
        speed: 0.2 + Math.random() * 0.3
      });
    }
    return temp;
  }, []);

  return (
    <group>
      {particles.map((particle, i) => (
        <FloatingParticle key={i} {...particle} index={i} />
      ))}
    </group>
  );
}

function FloatingParticle({ position, scale, speed, index }) {
  const mesh = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime() * speed;
    mesh.current.position.y = position[1] + Math.sin(t + index) * 0.3;
    mesh.current.position.x = position[0] + Math.cos(t * 0.5 + index) * 0.1;
  });

  return (
    <mesh ref={mesh} position={position}>
      <sphereGeometry args={[scale, 8, 8]} />
      <meshBasicMaterial color="#DAA520" transparent opacity={0.6} />
    </mesh>
  );
}

// Sun rays emanating from center
function SunRays() {
  const raysRef = useRef();
  
  useFrame((state) => {
    raysRef.current.rotation.z = state.clock.getElapsedTime() * 0.05;
  });

  const rays = useMemo(() => {
    const temp = [];
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      temp.push({ angle, length: 1.5 + Math.random() * 0.5 });
    }
    return temp;
  }, []);

  return (
    <group ref={raysRef}>
      {rays.map((ray, i) => (
        <mesh key={i} rotation={[0, 0, ray.angle]} position={[0, 0, -0.1]}>
          <planeGeometry args={[0.02, ray.length]} />
          <meshBasicMaterial 
            color="#DAA520" 
            transparent 
            opacity={0.15}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

// Orbiting certification component
function OrbitingText({ text, radius, speed, yOffset = 0 }) {
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime() * speed;
    ref.current.position.x = Math.cos(t) * radius;
    ref.current.position.z = Math.sin(t) * radius;
    ref.current.position.y = yOffset + Math.sin(t * 2) * 0.15;
    ref.current.rotation.y = -t;
  });

  return (
    <group ref={ref}>
      <Text
        fontSize={0.25}
        color="#8B4513"
        anchorX="center"
        anchorY="middle"
      >
        {text}
      </Text>
    </group>
  );
}

// Small orbiting stars/dots
function OrbitingStars() {
  const groupRef = useRef();
  
  useFrame((state) => {
    groupRef.current.rotation.y = state.clock.getElapsedTime() * 0.1;
    groupRef.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.05) * 0.1;
  });

  const stars = useMemo(() => {
    const temp = [];
    for (let i = 0; i < 20; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const radius = 4.5 + Math.random() * 1.5;
      temp.push({
        position: [
          radius * Math.sin(phi) * Math.cos(theta),
          radius * Math.sin(phi) * Math.sin(theta) * 0.4,
          radius * Math.cos(phi)
        ],
        scale: 0.03 + Math.random() * 0.04
      });
    }
    return temp;
  }, []);

  return (
    <group ref={groupRef}>
      {stars.map((star, i) => (
        <mesh key={i} position={star.position}>
          <sphereGeometry args={[star.scale, 6, 6]} />
          <meshBasicMaterial color="#C9A227" transparent opacity={0.5} />
        </mesh>
      ))}
    </group>
  );
}

// Central glowing core (Sun)
function GlowingCore() {
  const meshRef = useRef();
  const glowRef = useRef();
  
  useFrame((state) => {
    meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.15;
    const scale = 1 + Math.sin(state.clock.getElapsedTime() * 0.8) * 0.03;
    meshRef.current.scale.set(scale, scale, scale);
    
    // Pulsing glow
    const glowScale = 1 + Math.sin(state.clock.getElapsedTime() * 1.5) * 0.08;
    glowRef.current.scale.set(glowScale, glowScale, glowScale);
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.4}>
      <group>
        {/* Sun rays */}
        <SunRays />
        
        {/* Core sphere */}
        <mesh ref={meshRef}>
          <sphereGeometry args={[0.6, 64, 64]} />
          <meshStandardMaterial 
            color="#C9A227" 
            emissive="#DAA520" 
            emissiveIntensity={0.4}
            metalness={0.5}
            roughness={0.4}
          />
        </mesh>
        
        {/* Inner glow */}
        <mesh>
          <sphereGeometry args={[0.72, 32, 32]} />
          <meshBasicMaterial 
            color="#DAA520" 
            transparent 
            opacity={0.15}
          />
        </mesh>
        
        {/* Outer glow - pulsing */}
        <mesh ref={glowRef}>
          <sphereGeometry args={[0.9, 32, 32]} />
          <meshBasicMaterial 
            color="#F4D03F" 
            transparent 
            opacity={0.08}
          />
        </mesh>
        
        {/* Outermost halo */}
        <mesh>
          <sphereGeometry args={[1.1, 32, 32]} />
          <meshBasicMaterial 
            color="#C9A227" 
            transparent 
            opacity={0.04}
          />
        </mesh>
      </group>
    </Float>
  );
}

// Orbit rings
function OrbitRings() {
  const rings = useMemo(() => [
    { radius: 1.8, color: '#C9A227', opacity: 0.4 },
    { radius: 2.8, color: '#8B4513', opacity: 0.25 },
    { radius: 3.8, color: '#DAA520', opacity: 0.18 },
    { radius: 5, color: '#C9A227', opacity: 0.08 },
  ], []);

  return (
    <>
      {rings.map((ring, i) => (
        <mesh key={i} rotation={[Math.PI / 2.2, 0, i * 0.2]}>
          <ringGeometry args={[ring.radius - 0.012, ring.radius + 0.012, 128]} />
          <meshBasicMaterial 
            color={ring.color} 
            transparent 
            opacity={ring.opacity}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </>
  );
}

// Main 3D Scene - positioned to the left
function Scene() {
  return (
    <group position={[-4, 0.5, 0]}>
      <ambientLight intensity={0.9} />
      <pointLight position={[5, 5, 5]} intensity={0.7} color="#DAA520" />
      <pointLight position={[-5, -5, -5]} intensity={0.3} color="#C9A227" />
      
      <GlowingCore />
      <OrbitRings />
      <SunParticles />
      <OrbitingStars />
      
      {/* Sparkles around the sun */}
      <Sparkles 
        count={40}
        scale={8}
        size={1.5}
        speed={0.3}
        opacity={0.4}
        color="#DAA520"
      />
      
      {/* Orbiting certifications */}
      <OrbitingText text="ACCA" radius={1.8} speed={0.35} yOffset={0.15} />
      <OrbitingText text="CISA" radius={2.8} speed={0.25} yOffset={-0.2} />
      <OrbitingText text="CSOE" radius={3.8} speed={0.18} yOffset={0.1} />
    </group>
  );
}

export function HeroSection() {
  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #F7F3ED 0%, #EDE6DA 50%, #F5F0E6 100%)'
      }}
    >
      {/* 3D Canvas Background */}
      <div className="absolute inset-0 z-0">
        <Canvas
          camera={{ position: [0, 0, 10], fov: 50 }}
          gl={{ antialias: true, alpha: true }}
        >
          <Scene />
          <OrbitControls 
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.2}
            minPolarAngle={Math.PI / 2.5}
            maxPolarAngle={Math.PI / 1.8}
          />
        </Canvas>
      </div>

      {/* Content - positioned to the right */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6">
          <div className="ml-auto max-w-2xl text-right">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="space-y-6"
            >
              <motion.h1 
                data-testid="hero-title"
                className="font-heading text-6xl md:text-7xl lg:text-8xl font-bold italic"
                style={{ color: '#8B4513', textShadow: '0 2px 10px rgba(139, 69, 19, 0.1)' }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.8 }}
              >
                Sachin K
              </motion.h1>

              <motion.p 
                data-testid="hero-subtitle"
                className="font-body text-lg md:text-xl leading-relaxed"
                style={{ color: '#5D4E37' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
              >
                Risk Advisory Consultant by profession, self-trained coder, 
                <br className="hidden md:block" />
                full-time tinkerer, and an <span style={{ color: '#C9A227' }} className="font-semibold">Enthu-cutlet</span> from Bengaluru.
              </motion.p>

              <motion.div
                className="flex items-center justify-end gap-4 text-xs tracking-[0.3em] uppercase font-body"
                style={{ color: '#8B7355' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.6 }}
              >
                <span style={{ color: '#C9A227' }}>ACCA</span>
                <span style={{ color: '#C4B8A8' }}>•</span>
                <span style={{ color: '#8B4513' }}>CISA</span>
                <span style={{ color: '#C4B8A8' }}>•</span>
                <span style={{ color: '#A0522D' }}>CSOE</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToAbout}
        data-testid="scroll-down-btn"
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10 transition-colors animate-bounce"
        style={{ color: '#C9A227' }}
        whileHover={{ color: '#8B4513' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
      >
        <ChevronDown size={32} />
      </motion.button>
    </section>
  );
}
